import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import env from '../utils/env';
import './Feedback.css';

interface FeedbackData {
    subject: string;
    message: string;
    type: 'bug' | 'feature' | 'general' | 'improvement';
    priority: 'low' | 'medium' | 'high';
}

const Feedback: React.FC = () => {
    const { token, isAuthenticated } = useAuth();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitSuccess, setSubmitSuccess] = useState(false);
    const [submitError, setSubmitError] = useState<string | null>(null);
    
    const [formData, setFormData] = useState<FeedbackData>({
        subject: '',
        message: '',
        type: 'general',
        priority: 'medium'
    });

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        if (!isAuthenticated || !token) {
            setSubmitError('You must be logged in to submit feedback');
            return;
        }

        if (!formData.subject.trim() || !formData.message.trim()) {
            setSubmitError('Please fill in both subject and message');
            return;
        }

        setIsSubmitting(true);
        setSubmitError(null);
        setSubmitSuccess(false);

        try {
            const response = await axios.post(`${env.apiUrl}/feedback`, formData, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.status === 201) {
                setSubmitSuccess(true);
                setFormData({
                    subject: '',
                    message: '',
                    type: 'general',
                    priority: 'medium'
                });
            }
        } catch (error: any) {
            console.error('Error submitting feedback:', error);
            setSubmitError(
                error.response?.data?.error || 
                error.response?.data?.message || 
                'Failed to submit feedback. Please try again.'
            );
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!isAuthenticated) {
        return (
            <div className="feedback-container">
                <div className="feedback-not-authenticated">
                    <h2>Feedback</h2>
                    <p>Please log in to submit feedback.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="feedback-container">
            <div className="feedback-header">
                <h2>
                    <i className="fas fa-comments"></i>
                    Submit Feedback
                </h2>
                <p>Help us improve LuLarge by sharing your thoughts, reporting bugs, or suggesting new features.</p>
            </div>

            {submitSuccess && (
                <div className="feedback-success">
                    <i className="fas fa-check-circle"></i>
                    <h3>Thank you for your feedback!</h3>
                    <p>Your feedback has been submitted successfully. We'll review it and get back to you if needed.</p>
                </div>
            )}

            {submitError && (
                <div className="feedback-error">
                    <i className="fas fa-exclamation-triangle"></i>
                    <p>{submitError}</p>
                </div>
            )}

            <form onSubmit={handleSubmit} className="feedback-form">
                <div className="feedback-row">
                    <div className="feedback-field">
                        <label htmlFor="type">
                            <i className="fas fa-tag"></i>
                            Feedback Type
                        </label>
                        <select
                            id="type"
                            name="type"
                            value={formData.type}
                            onChange={handleInputChange}
                            className="feedback-select"
                        >
                            <option value="general">General Feedback</option>
                            <option value="bug">Bug Report</option>
                            <option value="feature">Feature Request</option>
                            <option value="improvement">Improvement Suggestion</option>
                        </select>
                    </div>

                    <div className="feedback-field">
                        <label htmlFor="priority">
                            <i className="fas fa-flag"></i>
                            Priority
                        </label>
                        <select
                            id="priority"
                            name="priority"
                            value={formData.priority}
                            onChange={handleInputChange}
                            className="feedback-select"
                        >
                            <option value="low">Low</option>
                            <option value="medium">Medium</option>
                            <option value="high">High</option>
                        </select>
                    </div>
                </div>

                <div className="feedback-field">
                    <label htmlFor="subject">
                        <i className="fas fa-heading"></i>
                        Subject
                        <span className="required">*</span>
                    </label>
                    <input
                        type="text"
                        id="subject"
                        name="subject"
                        value={formData.subject}
                        onChange={handleInputChange}
                        placeholder="Brief description of your feedback"
                        maxLength={200}
                        className="feedback-input"
                        required
                    />
                    <span className="char-count">{formData.subject.length}/200</span>
                </div>

                <div className="feedback-field">
                    <label htmlFor="message">
                        <i className="fas fa-comment-alt"></i>
                        Message
                        <span className="required">*</span>
                    </label>
                    <textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleInputChange}
                        placeholder="Provide detailed information about your feedback. For bug reports, please include steps to reproduce the issue."
                        maxLength={2000}
                        rows={8}
                        className="feedback-textarea"
                        required
                    />
                    <span className="char-count">{formData.message.length}/2000</span>
                </div>

                <div className="feedback-type-descriptions">
                    <div className="type-description">
                        <h4>Feedback Types:</h4>
                        <ul>
                            <li><strong>Bug Report:</strong> Report issues, errors, or unexpected behavior</li>
                            <li><strong>Feature Request:</strong> Suggest new features or functionality</li>
                            <li><strong>Improvement:</strong> Suggest enhancements to existing features</li>
                            <li><strong>General:</strong> Any other feedback, questions, or comments</li>
                        </ul>
                    </div>
                </div>

                <div className="feedback-actions">
                    <button
                        type="submit"
                        disabled={isSubmitting || !formData.subject.trim() || !formData.message.trim()}
                        className="feedback-submit-btn"
                    >
                        {isSubmitting ? (
                            <>
                                <i className="fas fa-spinner fa-spin"></i>
                                Submitting...
                            </>
                        ) : (
                            <>
                                <i className="fas fa-paper-plane"></i>
                                Submit Feedback
                            </>
                        )}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default Feedback;